// CheckboxGroupWithOther — checkbox list with an "Other" escape hatch.
// Ticking "Other" reveals a free-text input. The typed value is appended to
// the output array alongside the predefined selections — callers never need
// to treat it specially.

import { useState, useId, type JSX } from "react";

interface Option {
  value: string;
  label: string;
}

interface Props {
  options:           readonly Option[];
  value:             string[];
  onChange:          (v: string[]) => void;
  columns?:          2 | 3;
  scrollable?:       boolean;
  otherPlaceholder?: string;
  error?:            string;
}

export function CheckboxGroupWithOther({
  options,
  value,
  onChange,
  columns = 2,
  scrollable = false,
  otherPlaceholder = "Please specify…",
  error,
}: Props): JSX.Element {
  const inputId   = useId();
  const knownVals = options.map((o) => o.value);

  // Any value in the array that isn't in the predefined list is "other" text
  const otherValues = value.filter((v) => !knownVals.includes(v));

  // Track whether the Other checkbox is ticked separately from text content
  const [otherChecked, setOtherChecked] = useState(otherValues.length > 0);
  const [otherText,    setOtherText]    = useState(otherValues.join(", "));

  function toggleKnown(optValue: string): void {
    onChange(
      value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue],
    );
  }

  function handleOtherCheck(checked: boolean): void {
    setOtherChecked(checked);
    if (!checked) {
      // Remove all free-text values from the output
      setOtherText("");
      onChange(value.filter((v) => knownVals.includes(v)));
    }
    // If checked but no text yet — just show the input, don't push anything
  }

  function handleOtherText(raw: string): void {
    setOtherText(raw);
    const known   = value.filter((v) => knownVals.includes(v));
    const entries = raw.split(",").map((s) => s.trim()).filter(Boolean);
    onChange([...known, ...entries]);
  }

  const gridCls = columns === 3 ? "grid grid-cols-3 gap-2" : "grid grid-cols-2 gap-2";
  const wrapCls = scrollable ? `${gridCls} max-h-44 overflow-y-auto pr-1` : gridCls;

  return (
    <div>
      <div className={wrapCls}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={value.includes(opt.value)}
              onChange={() => toggleKnown(opt.value)}
              className="rounded border-slate-300 text-navy-700 focus:ring-navy-500"
            />
            {opt.label}
          </label>
        ))}

        {/* Other option */}
        <label className="flex items-center gap-2 text-sm text-slate-500 italic cursor-pointer">
          <input
            type="checkbox"
            checked={otherChecked}
            onChange={(e) => handleOtherCheck(e.target.checked)}
            className="rounded border-slate-300 text-navy-700 focus:ring-navy-500"
          />
          Other…
        </label>
      </div>

      {/* Text input — only visible when Other is ticked */}
      {otherChecked && (
        <input
          id={inputId}
          type="text"
          value={otherText}
          onChange={(e) => handleOtherText(e.target.value)}
          placeholder={otherPlaceholder}
          autoFocus
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-500"
        />
      )}

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
